import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react'
import { PageMeta } from '@/components/seo/PageMeta'
import { LandingLayout } from '@/components/layout/LandingLayout'
import { FormPage, FormField, formLinkClass } from '@/components/landing/EditorialForm'
import { useToast } from '@/components/ui/use-toast'
import { useAuth } from '@/lib/contexts/auth-context'
import { cn } from '@/lib/utils'
import {
  featureRequestService,
  categoryLabels,
  type FeatureRequestCategory,
  type SubmitFeatureRequestRequest,
} from '@/lib/api/services/featureRequests'

const categories: FeatureRequestCategory[] = [
  'UiUx',
  'WordAddin',
  'ExcelAddin',
  'PowerPointAddin',
  'OutlookAddin',
  'Api',
  'Integrations',
  'AiAutomation',
  'Workflows',
  'Templates',
  'Other',
]

export default function FeatureRequestSubmit() {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading } = useAuth()
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Other' as FeatureRequestCategory,
  })
  const { toast } = useToast()

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login?redirect=/feature-requests/submit')
    }
  }, [isAuthenticated, isLoading, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)

    try {
      const request: SubmitFeatureRequestRequest = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
      }

      await featureRequestService.submitFeatureRequest(request)
      setSubmitted(true)
    } catch (error) {
      toast({
        title: 'Submission failed',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <LandingLayout>
        <div className="flex min-h-[60vh] items-center justify-center" role="status">
          <Loader2 className="h-8 w-8 animate-spin text-[var(--color-landing-accent)]" aria-hidden />
          <span className="sr-only">Loading</span>
        </div>
      </LandingLayout>
    )
  }

  const backLink = (
    <Link to="/feature-requests" className={cn(formLinkClass, 'inline-flex items-center gap-2 text-sm')}>
      <ArrowLeft className="h-4 w-4" />
      Back to Feature Requests
    </Link>
  )

  const counterClass = 'mt-1.5 text-right text-xs tabular-nums text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]'

  return (
    <>
      <PageMeta
        title="Submit Feature Request | Templatean"
        description="Submit your feature request and help shape the future of Templatean."
      />

      <LandingLayout>
        {submitted ? (
          <FormPage
            title={['Request', 'submitted.']}
            lede={
              <p>
                Thank you for your feedback! Your feature request has been added to our board.
                The community can now vote on it, and our team will review it soon.
              </p>
            }
          >
            <div className="flex flex-wrap gap-4">
              <Link to="/feature-requests" className="landing-cta landing-cta--solid landing-cta--lg landing-btn-sweep">
                View all requests
                <ArrowRight className="h-5 w-5" />
              </Link>
              <button
                type="button"
                onClick={() => {
                  setSubmitted(false)
                  setFormData({ title: '', description: '', category: 'Other' })
                }}
                className="landing-cta landing-cta--outline landing-cta--lg landing-btn-sweep"
              >
                Submit another
              </button>
            </div>
          </FormPage>
        ) : (
          <FormPage
            title={['Submit a', 'feature request.']}
            lede={
              <p>
                Share your idea with us. Be as detailed as possible to help others understand your request.
              </p>
            }
          >
            <div className="mb-8">{backLink}</div>
            <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_18rem] lg:gap-12">
              <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                <FormField id="title" label="Feature title">
                  <input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Feature title"
                    required
                    maxLength={200}
                    aria-describedby="title-count"
                    className="landing-field"
                  />
                  <p id="title-count" className={counterClass}>{formData.title.length}/200 characters</p>
                </FormField>

                <FormField id="category" label="Category">
                  <select
                    id="category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value as FeatureRequestCategory })}
                    required
                    className="landing-field"
                  >
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {categoryLabels[cat]}
                      </option>
                    ))}
                  </select>
                </FormField>

                <FormField id="description" label="Description">
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="What problem does it solve? How would it work?"
                    required
                    rows={8}
                    maxLength={2000}
                    aria-describedby="description-count"
                    className="landing-field min-h-[14rem] normal-case leading-[1.5] font-[family-name:var(--font-landing-body)] placeholder:font-[family-name:var(--font-landing-display)]"
                  />
                  <p id="description-count" className={counterClass}>{formData.description.length}/2000 characters</p>
                </FormField>

                <div className="mt-4">
                  <button
                    type="submit"
                    disabled={submitting || !formData.title || !formData.description}
                    className="landing-cta landing-cta--solid landing-cta--lg landing-btn-sweep disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Submitting
                      </>
                    ) : (
                      <>
                        Submit request
                        <ArrowRight className="h-5 w-5" />
                      </>
                    )}
                  </button>
                </div>
              </form>

              <aside className="self-start border border-[var(--color-landing-gray-900)] p-6 dark:border-white/40">
                <h2 className="mb-4 font-[family-name:var(--font-landing-subdisplay)] text-lg font-semibold text-[var(--color-landing-gray-900)] dark:text-white">
                  Tips for a great feature request:
                </h2>
                <ul className="space-y-3 text-[15px] leading-snug text-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-300)]">
                  {[
                    "Be specific about what problem you're trying to solve",
                    'Explain how you currently work around the issue',
                    'Describe the ideal solution from your perspective',
                    'Include any relevant use cases or examples',
                  ].map((tip) => (
                    <li key={tip} className="relative pl-5 before:absolute before:left-0 before:top-[0.5em] before:h-1.5 before:w-1.5 before:bg-[var(--color-landing-accent)] before:content-['']">
                      {tip}
                    </li>
                  ))}
                </ul>
              </aside>
            </div>
          </FormPage>
        )}
      </LandingLayout>
    </>
  )
}
